import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import PriceLine from '@spa/shared/components/PriceLine';
import {
  CategoryName,
  PaymentsListWrapper,
  PaymentsListCategory,
  RemainingPayments,
  SubTitle,
  Title,
  TitleWrapperDesktop,
  TitleWrapperMobile,
  PaymentsListTotal,
  Wrapper
} from './styles';
import Separator from '@spa/shared/components/Unisystem/Separator';
import PiggyBankAlt from '@spa/shared/svg/piggy-bank-alt';
import ReserveWarning from '../ReserveWarning';
import { getPaymentsLists } from '@spa/shared/utils/paymentSummary';
import { useMedia } from '@spa/shared/hooks';

const PaymentSummary = ({ accommodationOffer, property, priceDetails }) => {
  const { t } = useTranslation();
  const { is } = useMedia();

  const { onBooking, onMoveIn, duringStay } = getPaymentsLists(accommodationOffer, property, priceDetails, t);

  return (
    <React.Fragment>
      <Wrapper>
        <RemainingPayments
          title={
            <RemainingPaymentsTitle
              title={t('When your booking is accepted')}
              subTitle={t('Through Uniplaces')}
              isMobile={is.mobile}
            />
          }
          description={<PaymentsList categories={onBooking.categories} totalAmount={onBooking.totalAmount} t={t} />}
          resume={onBooking.totalAmount}
          isInitiallyOpen
        />
        <Separator />
        {onMoveIn.categories.length ? (
          <React.Fragment>
            <RemainingPayments
              title={
                <RemainingPaymentsTitle
                  title={t('After booking is confirmed')}
                  subTitle={t('To landlord')}
                  isMobile={is.mobile}
                />
              }
              description={<PaymentsList categories={onMoveIn.categories} totalAmount={onMoveIn.totalAmount} t={t} />}
              resume={onMoveIn.totalAmount}
              isInitiallyOpen
            />
            <Separator />
          </React.Fragment>
        ) : null}
        {duringStay.categories.length ? (
          <React.Fragment>
            <RemainingPayments
              title={
                <RemainingPaymentsTitle title={t('Monthly rent')} subTitle={t('To landlord')} isMobile={is.mobile} />
              }
              description={
                <PaymentsList categories={duringStay.categories} totalAmount={duringStay.totalAmount} t={t} />
              }
              resume={duringStay.totalAmount}
            />
            <Separator />
          </React.Fragment>
        ) : null}
        <ReserveWarning
          icon={<PiggyBankAlt />}
          // eslint-disable-next-line prettier/prettier
          message={t('If you find it cheaper elsewhere, we\'ll refund the difference.')}
        />
      </Wrapper>
    </React.Fragment>
  );
};

const PaymentsList = ({ categories, totalAmount, t }) => {
  if (!categories.some(({ payments }) => payments?.length)) {
    return null;
  }

  return (
    <PaymentsListWrapper>
      {categories.map(({ name, payments }, index) => (
        <React.Fragment key={index}>
          <PaymentsListCategory>
            {name ? <CategoryName>{name}</CategoryName> : null}
            {payments.map(({ key, title, value, type, className }) => (
              <PriceLine key={key} title={title} value={value} type={type} className={className} />
            ))}
          </PaymentsListCategory>
          {index < categories.length - 1 ? <Separator /> : null}
        </React.Fragment>
      ))}
      <PaymentsListTotal>
        <Separator />
        <PriceLine bigger title={t('Total')} value={totalAmount} />
      </PaymentsListTotal>
    </PaymentsListWrapper>
  );
};

const RemainingPaymentsTitle = ({ title, subTitle, isMobile }) => {
  const TitleWrapper = useMemo(() => (isMobile ? TitleWrapperMobile : TitleWrapperDesktop), [isMobile]);

  return (
    <TitleWrapper>
      <Title>{title}</Title>
      {!isMobile ? '→' : null}
      {subTitle ? <SubTitle>{subTitle}</SubTitle> : null}
    </TitleWrapper>
  );
};

RemainingPaymentsTitle.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  isMobile: PropTypes.bool
};

PaymentsList.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  totalAmount: PropTypes.string,
  t: PropTypes.func
};

PaymentSummary.propTypes = {
  accommodationOffer: PropTypes.object.isRequired,
  property: PropTypes.object.isRequired,
  priceDetails: PropTypes.object.isRequired
};

export default PaymentSummary;
